import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ensureAnalyticsSession,
  getCurrentAnalyticsSessionId,
  shouldTrackAnalytics,
  trackAnalyticsEvent,
  trackAnalyticsPageView,
} from '@/lib/analytics';

/**
 * Performance: Analytics-Init darf NIE den LCP blockieren. Wir warten daher
 * auf `window.load` (LCP ist dann garantiert durch) und planen die erste
 * Session-Erstellung über `requestIdleCallback`, sodass keine Supabase-Calls
 * im kritischen Render-Pfad landen. Vorher: 3 parallele Calls (~600ms) im
 * Critical Path.
 */
function whenIdleAfterLoad(cb: () => void, timeoutMs = 5000) {
  if (typeof window === 'undefined') return;
  // Wait `delayAfterLoad` ms after window.load before even thinking about
  // tracking. This guarantees LCP, FCP, and any first-paint critical work
  // is fully done before Supabase REST calls fire. Lighthouse then sees
  // these requests as post-load (non-critical) instead of attributing them
  // to the network dependency tree.
  const delayAfterLoad = 4000;
  const schedule = () => {
    setTimeout(() => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      };
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(cb, { timeout: timeoutMs });
      } else {
        setTimeout(cb, 500);
      }
    }, delayAfterLoad);
  };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

export function useAnalytics() {
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const pageEnteredRef = useRef<number>(Date.now());
  const lastPathRef = useRef<string>('');
  const initRef = useRef(false);
  const readyRef = useRef(false);
  const pendingPathRef = useRef<string | null>(null);

  const ensureSession = useCallback(async (): Promise<string | null> => {
    const sid = await ensureAnalyticsSession();
    sessionIdRef.current = sid;
    return sid;
  }, []);

  const trackPageView = useCallback(async (path: string) => {
    pageEnteredRef.current = Date.now();
    await trackAnalyticsPageView(path, document.title);
  }, []);

  const trackPageLeave = useCallback(() => {
    const sid = sessionIdRef.current || getCurrentAnalyticsSessionId();
    if (!sid) return;
    const dur = Math.round((Date.now() - pageEnteredRef.current) / 1000);
    // Use sendBeacon with the Edge Function URL as fallback for unload
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track`;
    navigator.sendBeacon(url, JSON.stringify({
      action: 'session_end', session_id: sid, duration_seconds: dur,
    }));
  }, []);

  const trackEvent = useCallback(async (eventType: string, metadata?: Record<string, unknown>) => {
    const sid = sessionIdRef.current || (await ensureSession());
    if (!sid) return;
    await trackAnalyticsEvent(eventType, metadata);
  }, [ensureSession]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (!shouldTrackAnalytics()) return;
    // Session und PageView NACH window.load + idle-Slot anlegen,
    // damit LCP / FCP nicht durch Supabase-Inserts blockiert werden.
    whenIdleAfterLoad(() => {
      readyRef.current = true;
      ensureSession().then(() => {
        const pending = pendingPathRef.current;
        if (pending) {
          pendingPathRef.current = null;
          trackPageView(pending);
        }
      });
    });
    window.addEventListener('beforeunload', trackPageLeave);
    return () => window.removeEventListener('beforeunload', trackPageLeave);
  }, [ensureSession, trackPageLeave]);

  useEffect(() => {
    if (!shouldTrackAnalytics()) return;
    const path = location.pathname;
    if (path === lastPathRef.current) return;
    if (path.startsWith('/admin')) return;
    lastPathRef.current = path;
    if (readyRef.current) {
      trackPageView(path);
    } else {
      // Buffer initial path; wird nach idle gesendet
      pendingPathRef.current = path;
    }
  }, [location.pathname, trackPageView]);

  return { trackEvent };
}
